import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z, requiredEmailSchema } from '@dcp/shared'
import { productRoleSchema, PRODUCT_ROLE_LABELS } from '@dcp/shared'
import { Loader2, UserPlus } from 'lucide-react'
import { toast } from '@atrvd/saas-ui'
import { useCreateUserInvitation } from '@/hooks/useOrganizationInvitations'
import type { OrganizationInvitation } from '@/hooks/useOrganizationInvitations'
import { useOrganization } from '@/contexts/OrganizationContext'
import {
  Modal,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@atrvd/saas-ui'

/**
 * Form validation schema for inviting team members.
 * All fields are required; email must be a valid address;
 * product_role must be one of the four enum values.
 */
const inviteFormSchema = z.object({
  // Email goes through the shared requiredEmailSchema so it is trimmed,
  // lowercased and format-checked identically to every other form (DCP-151).
  email: requiredEmailSchema,
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  product_role: productRoleSchema,
})

/**
 * Raw form field type — email is plain string (pre-transform).
 * Used as TFieldValues for useForm<> so defaultValues and register() accept plain strings.
 * z.output<typeof inviteFormSchema>['email'] is the branded Email type.
 */
type InviteFormValues = z.input<typeof inviteFormSchema>


/**
 * Props for UserInviteForm component.
 *
 * onInviteCreated is called after the invitation row is committed and before
 * the modal closes. The parent page uses this to start email status polling
 * outside the modal lifecycle — the modal closes immediately (fast UX) while
 * the parent continues tracking delivery and fires toast notifications.
 */
interface UserInviteFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Called with the created invitation id and recipient email when the
   * invitation row is successfully committed. The modal will close right
   * after this callback returns, so the callback must be synchronous or
   * fire-and-forget (non-blocking).
   */
  onInviteCreated?: (invitationId: string, recipientEmail: string) => void
}

/**
 * Dialog form for inviting new team members to the organization.
 * Collects first name, last name, email, and product role.
 * Send Invitation is disabled until all fields pass validation.
 *
 * After the invitation row is committed the modal closes immediately and fires
 * onInviteCreated so the parent page can poll email delivery status. This keeps
 * the polling hook alive after the modal unmounts.
 */
export function UserInviteForm({ open, onOpenChange, onInviteCreated }: UserInviteFormProps) {
  const { currentOrganization } = useOrganization()
  const createInvitation = useCreateUserInvitation()

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState,
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    // Validate on first blur so errors appear as the user leaves each field
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      product_role: undefined,
    },
  })

  const { errors } = formState

  /**
   * Handle form submission.
   * Creates the invitation row, closes the modal immediately, then fires
   * onInviteCreated so the parent page can track email delivery via polling.
   * A "creating" optimistic toast is shown while the server call is in flight;
   * it is replaced by a status toast from the parent once delivery is known.
   */
  const onSubmit = async (data: InviteFormValues) => {
    if (!currentOrganization?.id) {
      toast.error('No organization selected')
      return
    }

    try {
      const result = await createInvitation.mutateAsync({
        admin_email: data.email,
        admin_first_name: data.firstName,
        admin_last_name: data.lastName,
        target_organization_id: currentOrganization.id,
        product_role: data.product_role,
      })

      // Optimistic feedback: close the modal right away so the user is not
      // blocked waiting for Trigger.dev to send the email (which is async).
      // The parent's polling hook will fire a terminal toast when email delivery
      // is confirmed or fails.
      toast.info('Invitation created. Sending email...')

      // Extract the created invitation from the RPC response.
      // The edge function returns { data: OrganizationInvitation }.
      const invitation = (result as { data: OrganizationInvitation } | null)?.data

      reset()
      onOpenChange(false)

      // Hand off to the parent page for status polling; called after close so
      // the modal is gone before any status toast arrives from the parent.
      if (invitation?.id && onInviteCreated) {
        onInviteCreated(invitation.id, data.email)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send invitation'
      toast.error(message)
    }
  }

  /**
   * Handle modal close — reset form state on close.
   */
  const handleClose = () => {
    reset()
    onOpenChange(false)
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Invite Team Member"
      description={`Send an invitation to join ${currentOrganization?.name || 'your organization'} as a team member.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!formState.isValid || createInvitation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {createInvitation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Send Invitation
              </>
            )}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">
            Email <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="colleague@example.com"
            autoComplete="email"
            {...register('email')}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* First name / Last name */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">
              First Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              placeholder="John"
              {...register('firstName')}
            />
            {errors.firstName && (
              <p className="text-sm text-destructive">{errors.firstName.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">
              Last Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lastName"
              placeholder="Doe"
              {...register('lastName')}
            />
            {errors.lastName && (
              <p className="text-sm text-destructive">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Product role select */}
        <div className="space-y-2">
          <Label htmlFor="product_role">
            Role <span className="text-destructive">*</span>
          </Label>
          {/*
           * Controller is required because Select is an uncontrolled
           * third-party component; react-hook-form cannot register it via ref.
           */}
          <Controller
            name="product_role"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onValueChange={(value) => field.onChange(value)}
              >
                <SelectTrigger id="product_role" onBlur={field.onBlur}>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PRODUCT_ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.product_role && (
            <p className="text-sm text-destructive">{errors.product_role.message}</p>
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
          <p>
            The invited user will receive an email with a link to set up their account.
            They will join with the selected role and access to this organization.
          </p>
        </div>
      </form>
    </Modal>
  )
}
